import {
  useMemo,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
  useCallback,
} from 'react';
import Plotly from 'plotly.js/dist/plotly';
import type { CorrelationClusterData } from '~/types/visualization';

// Dynamically import Plot to avoid bundling it in the main build
// This reduces build-time memory usage by code-splitting Plotly.js
const Plot = lazy(() => import('react-plotly.js'));

interface Props {
  data: CorrelationClusterData;
  width?: number;
  height?: number;
  metricType: 'drawdown' | 'beta';
  selectedTicker?: string | null;
}

const readCSSVar = (variable: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    variable,
  );
  return value?.trim() || fallback;
};

const getThemeColors = () => ({
  textPrimary: readCSSVar('--bq-text-primary', '#f3f4f6'),
  textSecondary: readCSSVar('--bq-text-secondary', '#d1d5db'),
  surfaceCard: readCSSVar('--bq-surface-card', '#111111'),
  surfaceElevated: readCSSVar('--bq-surface-elevated', '#1a1a1a'),
  borderMuted: readCSSVar('--bq-border-muted', '#2d2d2d'),
  positive: readCSSVar('--bq-color-positive', '#10b981'),
  warning: readCSSVar('--bq-color-warning-strong', '#f59e0b'),
  negative: readCSSVar('--bq-color-negative', '#ef4444'),
  accent: readCSSVar('--bq-color-info-strong', '#3b82f6'),
});

export const CorrelationClusterChart = ({
  data,
  width,
  height,
  metricType,
  selectedTicker,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<any>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const axisRangeRef = useRef<{ xRange: number; yRange: number } | null>(null);
  const [themeColors, setThemeColors] = useState(getThemeColors);
  const [zoomRange, setZoomRange] = useState<{
    xaxis: { range: [number, number] };
    yaxis: { range: [number, number] };
  } | null>(null);

  // Update theme colors when CSS variables change
  useEffect(() => {
    const updateColors = () => setThemeColors(getThemeColors());
    updateColors();
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  // Update dimensions on mount and resize
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        // Only update if dimensions actually changed
        setDimensions((prev) => {
          if (prev.width !== width || prev.height !== height) {
            return { width, height };
          }
          return prev;
        });
      }
    };

    const handleResize = () => {
      // Cancel any pending updates
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);

      // Update on next frame for smooth UI
      rafId = requestAnimationFrame(updateDimensions);

      // Also schedule delayed updates to catch window animations
      timeoutId = setTimeout(updateDimensions, 100);
      setTimeout(updateDimensions, 300);
      setTimeout(updateDimensions, 500);
    };

    // Initial update
    updateDimensions();
    setTimeout(updateDimensions, 100);

    // Listen to multiple events for reliability
    window.addEventListener('resize', handleResize);
    window.addEventListener('focus', updateDimensions);
    document.addEventListener('click', updateDimensions);

    // ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('focus', updateDimensions);
      document.removeEventListener('click', updateDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  // Zoom to selected ticker
  useEffect(() => {
    if (!selectedTicker) {
      setZoomRange(null);
      return;
    }

    const selectedNode = data.nodes.find((n) => n.ticker === selectedTicker);
    if (!selectedNode) return;

    const { x, y } = selectedNode.position;
    console.log('Zooming to:', selectedTicker, 'at position:', x, y);

    // Calculate zoom range (smaller range = more zoomed in)
    const range = 0.3; // Smaller value to zoom IN more

    // Update zoom state which will be applied to layout
    setZoomRange({
      xaxis: { range: [x - range, x + range] },
      yaxis: { range: [y - range, y + range] },
    });
  }, [selectedTicker, data.nodes]);

  // Handle plot relayout events to track zoom level and update text visibility
  const handleRelayout = useCallback((event: any) => {
    if (
      event['xaxis.range[0]'] !== undefined &&
      event['xaxis.range[1]'] !== undefined
    ) {
      const xRange = Math.abs(
        event['xaxis.range[1]'] - event['xaxis.range[0]'],
      );
      const yRange = Math.abs(
        event['yaxis.range[1]'] - event['yaxis.range[0]'],
      );

      axisRangeRef.current = { xRange, yRange };

      // Calculate if we should show text based on zoom level
      // Threshold: show text when axis range is small (more zoomed in)
      const avgRange = (xRange + yRange) / 2;
      const shouldShowText = avgRange < 3; // Adjust this threshold based on your data

      // Update text visibility using Plotly.restyle (doesn't reset zoom)
      const plotlyDiv = plotRef.current?.el;
      if (plotlyDiv) {
        const currentMode = plotlyDiv.data[plotlyDiv.data.length - 1]?.mode;
        const newMode = shouldShowText ? 'markers+text' : 'markers';

        // Only update if mode actually changed
        if (currentMode !== newMode) {
          Plotly.restyle(
            plotlyDiv,
            { mode: newMode },
            plotlyDiv.data.length - 1,
          );
        }
      }
    } else if (event['xaxis.autorange'] || event['yaxis.autorange']) {
      // Reset zoom tracking
      axisRangeRef.current = null;

      // Hide text when fully zoomed out
      const plotlyDiv = plotRef.current?.el;
      if (plotlyDiv) {
        Plotly.restyle(
          plotlyDiv,
          { mode: 'markers' },
          plotlyDiv.data.length - 1,
        );
      }
    }
  }, []);

  const { scatterTrace, edgeTraces } = useMemo(() => {
    const isSelected = (ticker: string) => selectedTicker === ticker;
    // Normalize metric values based on actual data range for better color distribution
    const metricValues = data.nodes
      .map((n) => (metricType === 'drawdown' ? n.drawdown_percent : n.beta))
      .filter((v) => !isNaN(v) && isFinite(v)); // Filter out NaN and Infinity

    const minMetric = metricValues.length > 0 ? Math.min(...metricValues) : 0;
    const maxMetric = metricValues.length > 0 ? Math.max(...metricValues) : 0;
    const metricRange = maxMetric - minMetric;

    const normalizedMetrics = data.nodes.map((n) => {
      const value = metricType === 'drawdown' ? n.drawdown_percent : n.beta;

      // Handle NaN or invalid values
      if (isNaN(value) || !isFinite(value)) return 0.5; // Gray/neutral color
      if (metricRange === 0) return 0.5; // All same, use middle color

      if (metricType === 'drawdown') {
        // For drawdown: worst (most negative) = 1 (red), best = 0 (green)
        return (maxMetric - value) / metricRange;
      } else {
        // For beta: low beta = 0 (green), high beta = 1 (red)
        return (value - minMetric) / metricRange;
      }
    });

    // Create edge traces (lines connecting correlated assets)
    // Only show very strong correlations (>0.85) to reduce clutter
    const edgeTraces = data.edges
      .filter((edge) => edge.correlation >= 0.85)
      .map((edge) => {
        const sourceNode = data.nodes.find((n) => n.ticker === edge.source);
        const targetNode = data.nodes.find((n) => n.ticker === edge.target);

        if (!sourceNode || !targetNode) return null;

        return {
          type: 'scatter' as const,
          mode: 'lines' as const,
          x: [sourceNode.position.x, targetNode.position.x],
          y: [sourceNode.position.y, targetNode.position.y],
          line: {
            color: themeColors.borderMuted,
            width: 1,
          },
          hoverinfo: 'skip' as const,
          showlegend: false,
        };
      })
      .filter(Boolean);

    // Create scatter trace (nodes) - show text when zoomed in
    const scatterTrace = {
      type: 'scatter' as const,
      mode: (selectedTicker ? 'markers+text' : 'markers') as const,
      x: data.nodes.map((n) => n.position.x),
      y: data.nodes.map((n) => n.position.y),
      text: data.nodes.map((n) => n.ticker),
      textposition: 'top center' as const,
      textfont: {
        size: 10,
        color: themeColors.textPrimary,
      },
      marker: {
        size: data.nodes.map((n) => Math.sqrt(n.allocation_percent) * 20),
        color: normalizedMetrics,
        colorscale: [
          [0, themeColors.positive],
          [0.5, themeColors.warning],
          [1, themeColors.negative],
        ],
        showscale: false,
        line: {
          color: data.nodes.map((n) =>
            isSelected(n.ticker)
              ? themeColors.accent
              : themeColors.textSecondary,
          ),
          width: data.nodes.map((n) => (isSelected(n.ticker) ? 4 : 2)),
        },
      },
      customdata: data.nodes.map((n) => ({
        company_name: n.company_name,
        allocation: n.allocation_percent,
        drawdown: n.drawdown_percent,
        beta:
          n.beta == null || isNaN(Number(n.beta)) || !isFinite(Number(n.beta))
            ? 'N/A'
            : Number(n.beta).toFixed(2),
        sector: n.sector,
        top_correlations: n.top_correlations,
      })),
      hovertemplate:
        '<b>%{text}</b><br>' +
        '%{customdata.company_name}<br>' +
        '<br>' +
        'Allocation: %{customdata.allocation:.2f}%<br>' +
        (metricType === 'drawdown'
          ? 'Drawdown: %{customdata.drawdown:.2f}%<br>'
          : 'Beta: %{customdata.beta}<br>') +
        'Sector: %{customdata.sector}<br>' +
        '<extra></extra>',
      showlegend: false,
    };

    return { scatterTrace, edgeTraces };
  }, [data, metricType, selectedTicker, themeColors]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {dimensions.width > 0 && dimensions.height > 0 && (
        <Suspense
          fallback={
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeColors.textPrimary,
              }}
            >
              Loading chart...
            </div>
          }
        >
          <Plot
            ref={plotRef}
            data={[...edgeTraces, scatterTrace] as any}
            layout={{
              width: dimensions.width,
              height: dimensions.height,
              title: {
                text: `${data.portfolio_name} - Correlation Cluster`,
                font: { size: 16, color: themeColors.textPrimary },
              },
              xaxis: {
                showgrid: false,
                zeroline: false,
                showticklabels: false,
                title: '',
                ...(zoomRange?.xaxis || {}),
              },
              yaxis: {
                showgrid: false,
                zeroline: false,
                showticklabels: false,
                title: '',
                scaleanchor: 'x',
                scaleratio: 1,
                ...(zoomRange?.yaxis || {}),
              },
              dragmode: 'pan',
              hovermode: 'closest',
              plot_bgcolor: themeColors.surfaceElevated,
              paper_bgcolor: themeColors.surfaceCard,
              margin: { t: 50, r: 20, b: 20, l: 20 },
            }}
            config={{
              // Navigation controls:
              // - Left-click + drag: Pan (move view)
              // - Mouse wheel: Zoom in/out
              // - Toolbar "Box Zoom" button + drag: Create zoom box
              // - Double-click: Reset to default view
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['lasso2d', 'select2d'],
              toImageButtonOptions: {
                format: 'png',
                filename: `correlation-cluster-${data.snapshot_id}`,
                height: 1200,
                width: 1600,
                scale: 2,
              },
              responsive: true,
              scrollZoom: true,
            }}
            onRelayout={handleRelayout}
          />
        </Suspense>
      )}
    </div>
  );
};
